"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartLegend, ChartLegendContent, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, XAxis, YAxis } from "recharts";
import { Database, Funnel, ShieldCheck, TrendingDown } from "lucide-react";
const chartData = [{
  batch: "Batch 1",
  volume: 2500,
  cost: 15
}, {
  batch: "Batch 2",
  volume: 4200,
  cost: 12
}, {
  batch: "Batch 3",
  volume: 6800,
  cost: 9
}, {
  batch: "Batch 4",
  volume: 8500,
  cost: 7
}, {
  batch: "Batch 5",
  volume: 12000,
  cost: 5
}];
const chartConfig = {
  volume: {
    label: "Lead Volume",
    color: "var(--chart-1)"
  },
  cost: {
    label: "Cost Per Lead ($)",
    color: "var(--chart-2)"
  }
};
const features = [{
  title: "Massive Volume",
  description: "Access thousands of historical records specifically targeted for home improvement, solar, and remodeling sectors.",
  icon: Database
}, {
  title: "Data Integrity",
  description: "Rigorous multi-point verification process filters out disconnected numbers and invalid emails before delivery.",
  icon: ShieldCheck
}, {
  title: "Cost Efficiency",
  description: "Significantly lower Cost Per Acquisition (CPA) designed for sales teams with high-capacity outbound dialing.",
  icon: TrendingDown
}];
export default function AgedLeadsSolutions() {
  return <section id="aged-leads-solutions" className="dark bg-background text-foreground relative overflow-hidden py-24">
      {}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-20">
        <div className="bg-primary/20 h-[500px] w-[500px] rounded-full blur-[120px]" />
      </div>
      <div className="bg-grid-white/[0.02] absolute inset-0 bg-[length:32px_32px]" />

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="mb-16 max-w-3xl">
          <div className="mb-4 inline-flex items-center gap-2 border border-border/50 bg-muted/50 px-3 py-1 text-sm font-medium text-muted-foreground backdrop-blur-sm">
            <Funnel className="h-4 w-4 text-primary" />
            <span>Strategic Data Acquisition</span>
          </div>
          <h2 className="mb-6 text-4xl font-bold tracking-tight md:text-5xl lg:text-6xl">
            Aged Leads Solutions
          </h2>
          <p className="text-lg text-muted-foreground md:text-xl">
            Scale your outreach with cost-effective, high-volume data. Our aged
            leads provide the raw fuel your sales team needs to dominate the home
            improvement sector, backed by our stringent quality filtering process.
          </p>
        </div>

        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
          {}
          <div className="flex flex-col justify-center space-y-10">
            <div className="space-y-4">
              <h3 className="text-2xl font-semibold tracking-tight">
                The Filtering Protocol
              </h3>
              <p className="leading-relaxed text-muted-foreground">
                Not all aged data is created equal. At Axebox Media, our aged
                leads undergo a strict scrubbing process before they ever reach
                your CRM. We cross-reference against national DNC registries,
                verify line connectivity, and segment by specific home
                improvement verticals like kitchen remodeling, bathroom
                remodeling, and solar installations to ensure maximum relevance.
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
              {features.map((feature, i) => <Card key={feature.title} data-index={i} className="border-border/50 bg-card/40 transition-colors hover:bg-card/60 backdrop-blur-md">
                  <CardHeader className="pb-4">
                    <div className="mb-4 inline-flex h-10 w-10 items-center justify-center bg-primary/10 text-primary">
                      <feature.icon className="h-5 w-5" />
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>)}
            </div>

            <div>
              <Button asChild size="lg" className="h-12 px-8 text-base">
                <Link to="/ReserveYourDemoPage">Explore Data Options</Link>
              </Button>
            </div>
          </div>

          {}
          <div className="flex flex-col justify-center">
            <Card className="border-border/50 bg-card/40 backdrop-blur-md">
              <CardHeader>
                <CardTitle>Volume vs. Cost Efficiency</CardTitle>
                <CardDescription>
                  Demonstrating the inverse relationship between data volume and
                  acquisition cost across our aged lead batches.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ChartContainer config={chartConfig} className="aspect-auto h-[400px] w-full">
                  <ComposedChart data={chartData} margin={{
                  top: 20,
                  right: 20,
                  bottom: 20,
                  left: 20
                }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--border)" opacity={0.5} />
                    <XAxis dataKey="batch" tickLine={false} axisLine={false} tickMargin={10} stroke="var(--muted-foreground)" />
                    <YAxis yAxisId="left" tickLine={false} axisLine={false} tickMargin={10} stroke="var(--muted-foreground)" tickFormatter={value => `${value / 1000}k`} />
                    <YAxis yAxisId="right" orientation="right" tickLine={false} axisLine={false} tickMargin={10} stroke="var(--muted-foreground)" tickFormatter={value => `$${value}`} />
                    <ChartTooltip cursor={{
                    fill: "var(--muted)",
                    opacity: 0.2
                  }} content={<ChartTooltipContent />} />
                    <ChartLegend content={<ChartLegendContent />} />
                    <Bar yAxisId="left" dataKey="volume" fill="var(--color-volume)" radius={[4, 4, 0, 0]} maxBarSize={50} />
                    <Line yAxisId="right" type="monotone" dataKey="cost" stroke="var(--color-cost)" strokeWidth={3} dot={{
                    r: 4,
                    fill: "var(--background)",
                    strokeWidth: 2
                  }} activeDot={{
                    r: 6
                  }} />
                  </ComposedChart>
                </ChartContainer>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}